import { produce } from "immer";
import React, { useContext, useEffect } from "react";
import styled from "styled-components";

import { RichContentDisplay } from "@examspark/common-ui";
import { scoreMatchTable } from "@examspark/score";
import { Stem, StemMatchTable } from "@examspark/types";

import Mark from "../../common/Mark";
import QuestionContext from "../../QuestionContext";
import Prompt from "../shared/Prompt";

const StyledTable = styled.table`
  border: solid 1px #ddd;
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
  background-color: white;

  p:first-child {
    margin-top: 0;
  }
  p:last-child {
    margin-bottom: 0;
  }

  tr {
    th,
    td {
      border-top: solid 1px #ddd;
    }
  }
  tr:first-child {
    th,
    td {
      border-top: none;
    }
  }
  tr:nth-child(odd) {
    td {
      background-color: #f7f7f7;
    }
    td:hover {
      background-color: #e7e7e7;
    }
  }

  th,
  td {
    width: 100%;
    text-align: center;
    padding: 0.7rem;
    transition: background-color 0.2s;
    word-wrap: break-word;
  }
  th:first-child,
  td:first-child {
    text-align: left;
  }

  td {
    cursor: pointer;
  }
  td:hover {
    background-color: #f2f2f2;
  }

  th {
    background-color: #f7f7f7;
    font-weight: 500;
  }
  th:not(:first-child) {
    width: 40%;
    max-width: 100px;
  }
`;

interface MarkProps {
  mark?: "correct" | "incorrect";
}

const Styled = styled.div<MarkProps>`
  display: flex;
  position: relative;
  padding: 2rem 1.5rem;
  border-top: solid 3px transparent;
  max-width: 75rem;

  ${(props) => {
    if (props.mark === "correct") {
      return `
      background-color: #e4f3e4;
      border-top: solid 3px #9cb39c;
      `;
    }
    if (props.mark === "incorrect") {
      return `
      background-color: #f3e2e2;
      border-top: solid 3px #b32e2b;
      `;
    }
  }}

  .markup {
    position: absolute;
    top: -11px;
    right: -8px;
  }
`;

export interface ItemMatchMatrixProps {
  multiple?: boolean;
  data: StemMatchTable;
}

const MatchTable: React.FC<React.PropsWithChildren<ItemMatchMatrixProps>> = (
  props,
) => {
  const [player, dispatchPlayer] = useContext(QuestionContext);

  // Reveal Key
  useEffect(() => {
    if (!props.data.scoring) return;
    if (!player.revealKey) return;

    const rsp: {
      [key: string]: string[];
    } = {};
    for (const stem of props.data.content.stems) {
      for (const opt of props.data.content.options) {
        if (props.data.scoring.matchOptions?.[stem.id]?.[opt.id]?.isKey) {
          if (!rsp[stem.id]) rsp[stem.id] = [];
          rsp[stem.id].push(opt.id);
        }
      }
    }

    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        score: true,
        response: {
          matchTable: rsp,
        },
      },
    });
  }, [player.revealKey]);

  const handleSelect = (stem: Stem.Option, option: Stem.Option) => {
    // console.log(player);
    // return;
    dispatchPlayer({
      type: "RESPONSE",
      data: {
        id: props.data.id,
        response: produce(
          player.response?.[props.data.id] || {},
          (draft: any) => {
            // clear exsiting selection if multiple selections are not allowed
            if (!props.data.config?.allowMultipleKeys) {
              if (draft.matchTable?.[stem.id]) {
                draft.matchTable[stem.id] = [];
              }
            }

            if (!draft.matchTable) draft.matchTable = {};
            if (!draft.matchTable?.[stem.id]) draft.matchTable[stem.id] = [];

            if (props.data.config?.allowMultipleKeys) {
              const findIndex = draft.matchTable?.[stem.id].findIndex(
                (o: string) => {
                  return o === option.id;
                },
              );
              if (findIndex !== -1) {
                draft.matchTable[stem.id].splice(findIndex, 1);
              } else {
                draft.matchTable[stem.id].push(option.id);
              }
            } else {
              if (draft.matchTable[stem.id].includes(option.id)) return;
              draft.matchTable[stem.id].push(option.id);
            }
          },
        ),
      },
    });
  };

  // Score responses
  let mark: "incorrect" | "correct" | undefined;
  if (player.scoreResponses) {
    mark = "correct";

    const score = scoreMatchTable(props.data, player.response?.[props.data.id]);
    if (score.actual < score.max) {
      mark = "incorrect";
    }
  }

  return (
    <>
      <Prompt data={props.data.content.prompt} />
      <Styled mark={mark}>
        {mark && <Mark type={mark} />}
        <StyledTable>
          <tr>
            <th>
              <RichContentDisplay
                content={props.data.content.optionsTitle?.body}
              />
            </th>
            {props.data.content.options.map((opt) => {
              return (
                <th key={opt.id}>
                  <RichContentDisplay content={opt.body || ""} />
                </th>
              );
            })}
          </tr>
          {props.data.content.stems.map((stem) => {
            return (
              <tr key={stem.id}>
                <td>
                  <RichContentDisplay content={stem.body} />
                </td>
                {props.data.content.options.map((opt) => {
                  return (
                    <td key={opt.id} onClick={() => handleSelect(stem, opt)}>
                      <input
                        checked={player.response?.[props.data.id]?.matchTable?.[
                          stem.id
                        ]?.includes(opt.id)}
                        onClick={(e) => {
                          // e.preventDefault();
                        }}
                        type={
                          props.data.config?.allowMultipleKeys
                            ? "checkbox"
                            : "radio"
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </StyledTable>
      </Styled>
    </>
  );
};

export default MatchTable;
